import { BookOpenIcon, CalendarPlusIcon, HelpIcon, TranslateIcon, VoiceIcon } from '@braskai/icons';

import type { NavigationItemProps } from '@/shared/ui/NavigationItem';

import { HELP_CENTER_LINK, RASK_BOOK_A_CALL_WITH_CS_LINK, WHITE_LABEL_SUPPORT_LINK } from '@/shared/constants/docLinks';
import { PATHS } from '@/shared/constants/routes/paths';
import { translate, TranslationKey } from '@/shared/i18n';
import { ChatIcon, showChat } from '@/shared/thirdParty/hubSpot';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PlanCategory } from '@/shared/types/Billing';

type GetNavOptionsProps = {
  hasTrialPlan?: boolean;
  isGlossaryFeatureVisible: boolean;
  isMobile?: boolean;
  isVoicePresetsFeatureVisible: boolean;
  pathname: string;
  planCategory?: PlanCategory;
  spaceId: string;
  t?: (key: TranslationKey) => string;
};

const defaultTranslate = (key: TranslationKey) => translate('en', key);

const getBookACallWithCSNavItem = (t: (key: TranslationKey) => string) => ({
  title: t('sidebar.bookCall'),
  dataTestId: 'sidebar-schedule-call-CS',
  icon: <CalendarPlusIcon height={16} width={16} />,
  onClick: () => {
    window.open(RASK_BOOK_A_CALL_WITH_CS_LINK, '_blank', 'noopener');
    MixpanelEvents.scheduleCallCSClick();
  },
});

export const getSpacePath = (path: string, spaceId?: string) => {
  if (!spaceId) return path;

  if (path === PATHS.homepage) {
    return `/spaces/${spaceId}`;
  }

  return `/spaces/${spaceId}${path.startsWith('/') ? path : `/${path}`}`;
};

const isSelected = (path: string, pathname: string) => {
  const currentPath = pathname.split('/').slice(3, 4).join('/') || '';
  const itemPath = path.split('/').slice(3, 4).join('/') || '';

  return currentPath === itemPath || (!itemPath && currentPath.includes(PATHS.folders));
};

export const getNavOptions = ({
  hasTrialPlan = false,
  isGlossaryFeatureVisible,
  isMobile = false,
  isVoicePresetsFeatureVisible,
  pathname,
  planCategory,
  spaceId,
  t = defaultTranslate,
}: GetNavOptionsProps) => {
  const navItems: NavigationItemProps[] = [
    {
      title: t('sidebar.translation'),
      dataTestId: 'sidebar-menu-translation',
      icon: <TranslateIcon height={16} width={16} />,
      path: getSpacePath(PATHS.homepage, spaceId),
      onClick: () => {
        MixpanelEvents.translationTabClick();
      },
    },
  ];

  if (isVoicePresetsFeatureVisible) {
    navItems.push({
      title: t('sidebar.voiceCloneLibrary'),
      dataTestId: 'sidebar-menu-voice-clone',
      icon: <VoiceIcon height={16} width={16} />,
      path: getSpacePath(PATHS.voices, spaceId),
      onClick: () => {
        MixpanelEvents.voiceCloneLibraryTabClick();
      },
    });
  }

  if (isGlossaryFeatureVisible) {
    navItems.push({
      title: t('sidebar.dictionary'),
      dataTestId: 'sidebar-menu-dictionary',
      icon: <BookOpenIcon height={16} width={16} />,
      isDisabled: isMobile,
      path: getSpacePath(PATHS.glossaries, spaceId),
      rightText: isMobile ? t('common.desktopOnly') : undefined,
      onClick: () => {
        MixpanelEvents.dictionaryTabClick();
      },
    });
  }

  const isEnterpriseOrBusiness = planCategory === PlanCategory.Enterprise || planCategory === PlanCategory.Business;

  // Trial users go through the new Book-a-Demo flow — hide the CS link to avoid double CTAs.
  if (isEnterpriseOrBusiness && !hasTrialPlan && isMobile) {
    navItems.push(getBookACallWithCSNavItem(t));
  }

  return navItems.map((item) => {
    const selected = isSelected(item.path || '', pathname);

    return {
      ...item,
      selected,
    };
  });
};

type GetHelpNavOptionsProps = {
  hasTrialPlan?: boolean;
  isMobile?: boolean;
  isWhiteLabel?: boolean;
  planCategory?: PlanCategory;
  t?: (key: TranslationKey) => string;
};

export const getHelpNavOptions = ({
  hasTrialPlan = false,
  isMobile = false,
  isWhiteLabel = false,
  planCategory,
  t = defaultTranslate,
}: GetHelpNavOptionsProps): NavigationItemProps[] => {
  const navItems: NavigationItemProps[] = [];

  if (isWhiteLabel) {
    navItems.push({
      title: t('sidebar.getHelp'),
      dataTestId: 'sidebar-question-button',
      icon: <HelpIcon height={16} width={16} />,
      onClick: () => {
        window.open(`mailto:${WHITE_LABEL_SUPPORT_LINK}`);
      },
    });

    return navItems;
  }

  const isEnterpriseOrBusiness = planCategory === PlanCategory.Enterprise || planCategory === PlanCategory.Business;

  // Trial users go through the new Book-a-Demo flow — hide the CS link to avoid double CTAs.
  if (isEnterpriseOrBusiness && !hasTrialPlan && !isMobile) {
    navItems.push(getBookACallWithCSNavItem(t));
  }

  navItems.push(
    {
      title: t('sidebar.helpCenter'),
      dataTestId: 'sidebar-help-center',
      icon: <HelpIcon height={16} width={16} />,
      onClick: () => {
        MixpanelEvents.helpCenterClick();
        window.open(HELP_CENTER_LINK);
      },
    },
    {
      title: t('sidebar.getHelp'),
      dataTestId: 'sidebar-question-button',
      icon: <ChatIcon height={16} width={16} />,
      onClick: () => {
        MixpanelEvents.getHelpClick();
        showChat();
      },
    },
  );

  return navItems;
};

export const getPromoNavOptions = (
  showViewPlans: boolean,
  t: (key: TranslationKey) => string = defaultTranslate,
): NavigationItemProps[] => {
  if (!showViewPlans) return [];

  return [
    {
      title: t('sidebar.viewPlans'),
      dataTestId: 'sidebar-view-plans',
      isOutlined: true,
      path: `/${PATHS.pricing}`,
      onClick: () => {
        MixpanelEvents.viewPlansSidebarClick();
      },
    },
  ];
};
